/* eslint-disable func-names */
import RepartitionData from "@/domain/entities/dashboard/RepartitionData";
import { smartLabelColors } from "@/domain/enums/SmartLabel";
import { Z_INDEX } from "@/lib/zIndex";
import { smartLabelRootUrl } from "@/router/routes";
import {
    forceCenter,
    forceCollide,
    forceManyBody,
    forceSimulation,
    max,
    scaleSqrt,
    select,
    Simulation,
    SimulationNodeDatum,
} from "d3";
import { memo, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { useNavigate } from "react-router-dom";
import TooltipContent from "./TooltipContent";

type BubbleChartProps = {
    data: RepartitionData[];
};

// Type pour les nodes D3 avec les propriétés nécessaires pour la simulation
type D3Node = RepartitionData & SimulationNodeDatum;

const WIDTH = 140;
const HEIGHT = 110;

const BubbleChart = memo(({ data }: BubbleChartProps) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const navigate = useNavigate();

    // Simulation et positions PERSISTANTES : conservées entre deux mises à jour de `data`.
    // Ainsi un changement de valeur fait "grossir/rétrécir" les bulles existantes au lieu de
    // vider le SVG et de relancer la simulation depuis des positions aléatoires à chaque render
    // (c'était l'effet de "jonglage" — AB#1710). Le rendu visuel et les interactions restent
    // identiques à l'implémentation d'origine.
    const simulationRef = useRef<Simulation<D3Node, undefined> | null>(null);
    const nodesRef = useRef<D3Node[]>([]);

    // Créer la simulation de force UNE seule fois (elle vit toute la durée du composant).
    useEffect(() => {
        if (!svgRef.current) return undefined;

        const svg = select(svgRef.current)
            .attr("width", WIDTH)
            .attr("height", HEIGHT)
            .attr("margin", 10);

        const simulation = forceSimulation<D3Node>([])
            .force("charge", forceManyBody().strength(10)) // Réduit à 10 pour moins de mouvement
            .force("center", forceCenter(WIDTH / 2, HEIGHT / 2))
            .alphaDecay(0.1) // Augmente le taux de décroissance pour arrêter plus rapidement
            .velocityDecay(0.8); // Ajoute de la friction pour ralentir les mouvements

        // Mettre à jour la position des cercles à chaque tick
        simulation.on("tick", () => {
            svg.selectAll<SVGCircleElement, D3Node>("circle")
                .attr("cx", (d) => d.x || 0)
                .attr("cy", (d) => d.y || 0);
        });

        simulationRef.current = simulation;

        return () => {
            simulation.stop();
            simulationRef.current = null;
        };
    }, []);

    useEffect(() => {
        const simulation = simulationRef.current;
        if (!data || !svgRef.current || !simulation) return undefined;

        const svg = select(svgRef.current);

        // Réconcilier les nodes avec `data` PAR id en conservant les positions existantes
        // (aucune position aléatoire -> pas de téléportation des bulles déjà affichées).
        const previousById = new Map(
            nodesRef.current.map((node) => [node.id, node]),
        );
        const nodes: D3Node[] = data.map((d) => {
            const existing = previousById.get(d.id);
            if (existing) {
                existing.value = d.value; // on rafraîchit la valeur, on garde x/y/vitesse
                return existing;
            }
            // Nouvelle bulle : démarre au centre (déterministe, jamais aléatoire)
            return { ...d, x: WIDTH / 2, y: HEIGHT / 2 };
        });
        nodesRef.current = nodes;

        // Créer le conteneur pour le tooltip (même cycle de vie que l'implémentation d'origine)
        const tooltipDiv = select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("position", "fixed")
            .style("background-color", "#35363B")
            .style("color", "white")
            .style("padding", "4px")
            .style("padding-right", "8px")
            .style("border-radius", "16px")
            .style("font-size", "12px")
            .style("pointer-events", "none")
            .style("z-index", String(Z_INDEX.overlay))
            .style("max-width", "300px")
            .style("word-wrap", "break-word")
            .style("opacity", 0);

        // Créer le root React pour le tooltip
        const tooltipRoot = createRoot(tooltipDiv.node() as HTMLElement);

        // Créer une échelle pour la taille des bulles
        const radiusScale = scaleSqrt()
            .domain([0, max(nodes, (d: { value: number }) => d.value) ?? 0])
            .range([7, 22]);

        // Join PAR id : chaque SmartLabel conserve son <circle> d'une mise à jour à l'autre
        // (enter/exit uniquement quand un label apparaît/disparaît réellement).
        const circles = svg
            .selectAll<SVGCircleElement, D3Node>("circle")
            .data(nodes, (d) => (d as D3Node).id)
            .join(
                (enter) =>
                    enter
                        .append("circle")
                        .attr("cx", (d) => d.x ?? WIDTH / 2)
                        .attr("cy", (d) => d.y ?? HEIGHT / 2)
                        .attr("r", (d) => radiusScale(d.value)),
                (update) => update,
                (exit) => exit.remove(),
            )
            .style("fill", (d) => smartLabelColors[d.id])
            .style("cursor", "pointer")
            .style("stroke", "transparent")
            .style("stroke-width", "3px");

        // (Ré)attacher les handlers à chaque update pour qu'ils pointent vers le tooltip courant
        // -> visuel/interactions strictement identiques à l'origine (bordure, tooltip, clic).
        circles
            .on(
                "mouseover",
                function (
                    event: { clientX: number; clientY: number },
                    d: RepartitionData,
                ) {
                    const circle = select(this);

                    // Ajouter la bordure
                    circle
                        .style("stroke", "#666")
                        .transition()
                        .duration(100)
                        .attr("opacity", 0.8);

                    // Calculer la position du tooltip en évitant les débordements
                    const tooltipWidth = 300; // Largeur estimée du tooltip
                    const tooltipHeight = 60; // Hauteur estimée du tooltip
                    const margin = 10;

                    let left = event.clientX + margin;
                    let top = event.clientY - margin;

                    // Vérifier les débordements à droite
                    if (left + tooltipWidth > window.innerWidth) {
                        left = event.clientX - tooltipWidth - margin;
                    }

                    // Vérifier les débordements en bas
                    if (top + tooltipHeight > window.innerHeight) {
                        top = event.clientY - tooltipHeight - margin;
                    }

                    // Vérifier les débordements à gauche et en haut
                    left = Math.max(margin, left);
                    top = Math.max(margin, top);

                    // Afficher le tooltip avec JSX
                    tooltipRoot.render(<TooltipContent data={d} />);

                    tooltipDiv
                        .style("opacity", 1)
                        .style("left", `${left}px`)
                        .style("top", `${top}px`);
                },
            )
            .on("click", (_event, d: RepartitionData) => {
                navigate(`${smartLabelRootUrl}/${d.id}`);
            })
            .on("mousemove", (event: { clientX: number; clientY: number }) => {
                // Déplacer le tooltip avec la souris en respectant les limites
                const tooltipWidth = 200;
                const tooltipHeight = 60;
                const margin = 10;

                let left = event.clientX + margin;
                let top = event.clientY - margin;

                // Vérifier les débordements
                if (left + tooltipWidth > window.innerWidth) {
                    left = event.clientX - tooltipWidth - margin;
                }
                if (top + tooltipHeight > window.innerHeight) {
                    top = event.clientY - tooltipHeight - margin;
                }

                left = Math.max(margin, left);
                top = Math.max(margin, top);

                tooltipDiv.style("left", `${left}px`).style("top", `${top}px`);
            })
            .on("mouseout", function () {
                // Retirer la bordure
                select(this)
                    .style("stroke", "transparent")
                    .transition()
                    .duration(200)
                    .attr("opacity", 1);

                // Cacher le tooltip
                tooltipDiv.style("opacity", 0);
            });

        // Faire varier le rayon en douceur (enter + update) au lieu de sauter brutalement
        circles
            .transition()
            .duration(300)
            .attr("r", (d) => radiusScale(d.value));

        // Alimenter la simulation persistante avec les nodes réconciliés, ajuster la collision
        // à la nouvelle taille puis relancer doucement SANS réinitialiser les positions.
        simulation.nodes(nodes);
        simulation.force(
            "collision",
            forceCollide<D3Node>().radius((d) => radiusScale(d.value)),
        );
        simulation.alpha(0.3).restart();

        // Nettoyer le tooltip de cette mise à jour (même logique que l'origine)
        return () => {
            setTimeout(() => {
                tooltipRoot.unmount();
                tooltipDiv.remove();
            }, 0);
        };
    }, [data, navigate]);

    return <svg ref={svgRef} />;
});

BubbleChart.displayName = "BubbleChart";

export default BubbleChart;
